import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { serviceNavItems } from '../../../data/services';
import { TELEGRAM_URL, WHATSAPP_URL } from '../../../constants/contact';
import ShareButtons from '../../common/ShareButtons';
import { getRssFeedUrl } from '../../../constants/rss';

const LOGO = 'http://geel.fp.08wx.cn/images/e33b4806-4fc4-48a2-93dd-68e0d55a6c41.png';

export default function Footer() {
  const { t, i18n } = useTranslation();
  const rssUrl = getRssFeedUrl(i18n.language);
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);

  const quickLinks = [
    { labelKey: 'nav.home', path: '/' },
    { labelKey: 'nav.about', path: '/about' },
    { labelKey: 'nav.advantages', path: '/advantages' },
    { labelKey: 'nav.news', path: '/news' },
    { labelKey: 'nav.contact', path: '/contact' },
  ];

  const productLinks = [
    { labelKey: 'nav.products.overview', path: '/products' },
    { labelKey: 'nav.products.cex', path: '/products/cex' },
    { labelKey: 'nav.products.rwa', path: '/products/rwa' },
    { labelKey: 'nav.products.wallet', path: '/products/wallet' },
    { labelKey: 'nav.products.staking', path: '/products/staking' },
    { labelKey: 'nav.products.mining', path: '/products/mining' },
  ];

  const serviceLinks = [
    { labelKey: 'nav.services.overview', path: '/services' },
    ...serviceNavItems.map((item) => ({ labelKey: item.labelKey, path: item.path })),
  ];

  const handleSubscribe = (e: React.FormEvent) => {
    e.preventDefault();
    if (email) {
      setSubscribed(true);
      setEmail('');
    }
  };

  return (
    <footer className="bg-[#FFF5F8] border-t border-primary-200">
      <div className="max-w-[1400px] mx-auto px-6 pt-16 pb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 mb-12">
          {/* Col 1: Brand */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-9 h-9 flex items-center justify-center">
                <img src={LOGO} alt="Logo" className="w-full h-full object-contain" />
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-6">
              {t('footer.about')}
            </p>

            {/* Subscribe */}
            <p className="text-xs text-gray-400 uppercase tracking-wider mb-3">{t('footer.subscribe')}</p>
            {subscribed ? (
              <div className="flex items-center gap-2 text-primary text-sm">
                <i className="ri-checkbox-circle-line" />
                <span>{t('footer.subscribe.success')}</span>
              </div>
            ) : (
              <form onSubmit={handleSubscribe} className="flex">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('footer.subscribe.placeholder')}
                  className="flex-1 bg-white border border-primary-200 text-gray-700 text-sm px-4 py-2.5 rounded-l-lg focus:outline-none focus:border-primary/50 min-w-0"
                />
                <button
                  type="submit"
                  className="bg-primary hover:bg-primary-dark text-white font-semibold text-sm px-4 py-2.5 rounded-r-lg transition-colors whitespace-nowrap cursor-pointer"
                >
                  {t('footer.subscribe.btn')}
                </button>
              </form>
            )}

            <a
              href={rssUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 inline-flex items-center gap-2 text-sm text-gray-500 hover:text-primary transition-colors"
            >
              <i className="ri-rss-line text-base" />
              <span>{t('footer.rss')}</span>
            </a>

            {/* Social — share current page */}
            <ShareButtons variant="footer" className="mt-5" />
          </div>

          {/* Col 2: Quick Links */}
          <div>
            <h4 className="text-sm font-semibold text-gray-800 uppercase tracking-wider mb-5">
              {t('footer.links')}
            </h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="flex items-center gap-2 text-sm text-gray-500 hover:text-primary transition-colors duration-150 group"
                  >
                    <i className="ri-arrow-right-s-line text-primary/50 group-hover:text-primary transition-colors" />
                    {t(link.labelKey)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Col 3: Products & Services */}
          <div>
            <h4 className="text-sm font-semibold text-gray-800 uppercase tracking-wider mb-5">
              {t('footer.products')}
            </h4>
            <ul className="space-y-3 mb-8">
              {productLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="flex items-center gap-2 text-sm text-gray-500 hover:text-primary transition-colors duration-150 group"
                  >
                    <i className="ri-arrow-right-s-line text-primary/50 group-hover:text-primary transition-colors" />
                    {t(link.labelKey)}
                  </Link>
                </li>
              ))}
            </ul>
            <h4 className="text-sm font-semibold text-gray-800 uppercase tracking-wider mb-5">
              {t('footer.services')}
            </h4>
            <ul className="space-y-3">
              {serviceLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="flex items-center gap-2 text-sm text-gray-500 hover:text-primary transition-colors duration-150 group"
                  >
                    <i className="ri-arrow-right-s-line text-primary/50 group-hover:text-primary transition-colors" />
                    {t(link.labelKey)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Col 4: Contact */}
          <div>
            <h4 className="text-sm font-semibold text-gray-800 uppercase tracking-wider mb-5">
              {t('footer.contact')}
            </h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-map-pin-line text-primary text-sm" />
                </div>
                <span className="text-sm text-gray-500 leading-relaxed">{t('footer.address')}</span>
              </li>
              <li className="flex items-center gap-3">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                  <i className="ri-mail-line text-primary text-sm" />
                </div>
                <a href={`mailto:${t('footer.email')}`} rel="nofollow" className="text-sm text-gray-500 hover:text-primary transition-colors">
                  {t('footer.email')}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                  <i className="ri-telegram-line text-primary text-sm" />
                </div>
                <a
                  href={TELEGRAM_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-gray-500 hover:text-primary transition-colors"
                >
                  {t('footer.telegram')}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                  <i className="ri-whatsapp-line text-primary text-sm" />
                </div>
                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-gray-500 hover:text-primary transition-colors"
                >
                  {t('footer.whatsapp')}
                </a>
              </li>
            </ul>

            {/* Trust Badges */}
            <div className="mt-6 grid grid-cols-2 gap-2">
              {[
                { icon: 'ri-shield-check-line', label: t('footer.badge.security') },
                { icon: 'ri-building-line', label: t('footer.badge.singapore') },
                { icon: 'ri-time-line', label: t('footer.badge.experience') },
                { icon: 'ri-customer-service-2-line', label: t('footer.badge.support') },
              ].map((badge, i) => (
                <div key={i} className="flex items-center gap-2 bg-white border border-primary-200 rounded-lg px-3 py-2">
                  <i className={`${badge.icon} text-primary text-xs`} />
                  <span className="text-xs text-gray-500 whitespace-nowrap">{badge.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-primary-200 pt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-gray-400">{t('footer.copyright')}</p>
          <div className="flex items-center gap-6">
            <a href="#" rel="nofollow" className="text-xs text-gray-400 hover:text-primary transition-colors">{t('footer.privacy')}</a>
            <a href="#" rel="nofollow" className="text-xs text-gray-400 hover:text-primary transition-colors">{t('footer.terms')}</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
